import React, { useEffect, useRef, useState } from 'react';

import { ORDER_SECTION_IDS, OrderSectionKey, orderItemCardId } from '../constants';
import { Bar, Steps, ItemsMenu, ItemsMenuEntry, ItemsMenuStatus, StepButton } from './OrderStickyStepper.style';

// o container que rola é o ancestral rolável mais próximo da barra (overflow-y: auto) — a
// aplicação não rola no window, e o container muda de nome por tenant.
const findScrollableAncestor = (element: HTMLElement): HTMLElement | null => {
  let node = element.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
};

export interface StepperItemEntry {
  index: number
  label: string
  status: string
  locked?: boolean
}

interface Props {
  itemsLabel: string
  itemEntries: StepperItemEntry[]
  onSelectItem: (index: number) => void
}

const OrderStickyStepper: React.FC<Props> = ({ itemsLabel, itemEntries, onSelectItem }) => {
  const [active, setActive] = useState<OrderSectionKey>('main');
  const [itemsMenuOpen, setItemsMenuOpen] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  // Números pelo índice, não hardcoded: reordenar ou (des)incluir um passo não pede editar rótulo.
  const sections: { key: OrderSectionKey; label: string }[] = [
    { key: 'main', label: 'Dados' },
    { key: 'items', label: itemsLabel },
    { key: 'review', label: 'Revisão' },
    { key: 'shipping', label: 'Frete' },
    { key: 'payment', label: 'Pagamento' },
  ];

  const steps: { key: OrderSectionKey; label: string }[] = sections.map((s, index) =>
    s.key === 'items' ? s : { key: s.key, label: `${index + 1} · ${s.label}` },
  );

  // Scrollspy por listener de scroll em FASE DE CAPTURA (document, true): o scroll da
  // aplicação acontece num container interno do layout, e a captura enxerga qualquer
  // container. Recalcula por retângulo a cada scroll/resize — robusto a mudanças de
  // altura do DOM (abrir/fechar cards), que deixavam o IntersectionObserver com estado
  // velho (teste E2E 2026-07-08).
  useEffect(() => {
    const keys = Object.keys(ORDER_SECTION_IDS) as OrderSectionKey[];
    if (barRef.current) scrollContainerRef.current = findScrollableAncestor(barRef.current);

    const compute = () => {
      let best: OrderSectionKey | null = null;
      let bestDist = Infinity;
      // Presente por DOM, não pela ordem (só ilustrativa) de ORDER_SECTION_IDS.
      let lastPresent: OrderSectionKey | null = null;
      let lastPresentTop = -Infinity;

      keys.forEach((k) => {
        const el = document.getElementById(ORDER_SECTION_IDS[k]);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top > lastPresentTop) {
          lastPresentTop = rect.top;
          lastPresent = k;
        }
        if (rect.bottom < 80) return; // seção já passou por completo
        // Ativa a seção cujo topo está mais perto da faixa de leitura (~100px) —
        // distância absoluta, para seções curtas no fim da página também vencerem.
        const dist = Math.abs(rect.top - 100);
        if (dist < bestDist) {
          bestDist = dist;
          best = k;
        }
      });

      // No fim do scroll a última seção não tem página abaixo para subir até a faixa de
      // leitura: a distância absoluta erra e acende a penúltima. Fim de scroll é caso à
      // parte, decidido pelo container, não pelo cálculo de distância acima.
      const container = scrollContainerRef.current;
      let atBottom = false;
      if (container && container.scrollHeight > container.clientHeight + 2) {
        atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 2;
      }
      if (atBottom && lastPresent) {
        setActive(lastPresent);
        return;
      }

      if (best) setActive(best);
    };

    compute();
    document.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      document.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, []);

  const goTo = (key: OrderSectionKey) => {
    if (key === 'items' && itemEntries.length > 0) {
      setItemsMenuOpen((open) => !open);
      return;
    }
    setItemsMenuOpen(false);
    setActive(key); // feedback imediato; o scrollspy assume após a rolagem
    document.getElementById(ORDER_SECTION_IDS[key])?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goToItem = (index: number) => {
    setItemsMenuOpen(false);
    onSelectItem(index);
    // Espera o card expandir antes de rolar.
    setTimeout(() => {
      document.getElementById(orderItemCardId(index))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  return (
    <Bar ref={barRef}>
      <Steps>
        {steps.map((s) => (
          <StepButton key={s.key} type="button" active={active === s.key} onClick={() => goTo(s.key)}>
            {s.label}
          </StepButton>
        ))}
        {itemsMenuOpen && (
          <ItemsMenu>
            {itemEntries.map((entry) => (
              <ItemsMenuEntry key={entry.index} type="button" onClick={() => goToItem(entry.index)}>
                <span>{entry.label}</span>
                <ItemsMenuStatus locked={entry.locked}>{entry.status}</ItemsMenuStatus>
              </ItemsMenuEntry>
            ))}
          </ItemsMenu>
        )}
      </Steps>
    </Bar>
  );
};

export default OrderStickyStepper;
